import posthog from 'posthog-js'
import type { EventName, BaseEventProperties } from '@/posthog.config'

const PUBLIC_KEY = (import.meta.env as any).VITE_PUBLIC_POSTHOG_KEY as string | undefined
const PUBLIC_HOST = (import.meta.env as any).VITE_PUBLIC_POSTHOG_HOST as string | undefined
const KEY = import.meta.env.VITE_POSTHOG_KEY ?? PUBLIC_KEY
const HOST = import.meta.env.VITE_POSTHOG_HOST ?? PUBLIC_HOST ?? 'https://us.i.posthog.com'
const ENABLED = (import.meta.env.VITE_POSTHOG_ENABLED === 'true') || Boolean(PUBLIC_KEY)

let initialized = false

function isBrowser() {
  return typeof window !== 'undefined'
}

export function initPostHog(): void {
  if (!isBrowser() || !ENABLED || !KEY) {
    return
  }

  if (initialized) {
    return
  }

  posthog.init(KEY, {
    api_host: HOST,
    autocapture: true,
    capture_pageview: false,
    capture_pageleave: true,
    session_recording: {
      maskAllText: false,
      maskTextSelector: '[data-ph-mask=true],input[type="password"]',
    },
    sanitize_properties: (properties: Record<string, unknown>) => {
      const cloned: Record<string, unknown> = { ...properties }
      const piiKeys = ['email', 'phone', 'first_name', 'last_name']
      piiKeys.forEach((key) => {
        if (key in cloned) {
          delete cloned[key]
        }
      })
      return cloned
    },
    loaded: (client) => {
      if (import.meta.env.DEV) {
        client.debug()
      }
    },
  })
  initialized = true
}

export function identify(userId: string, properties?: Record<string, unknown>): void {
  if (!ENABLED || !isBrowser()) {
    return
  }
  posthog.identify(userId, properties)
}

export function groupOrganization(orgId: string, properties?: Record<string, unknown>): void {
  if (!ENABLED || !isBrowser()) {
    return
  }
  posthog.group('organization', orgId, properties)
}

export function registerGlobals(properties: Record<string, unknown>): void {
  if (!ENABLED || !isBrowser()) {
    return
  }
  posthog.register(properties)
}

export function capture(
  event: EventName,
  properties?: BaseEventProperties & Record<string, unknown>
): void {
  if (!ENABLED || !isBrowser()) {
    return
  }
  posthog.capture(event, properties)
}

export function pageview(path?: string): void {
  if (!ENABLED || !isBrowser()) {
    return
  }
  posthog.capture('$pageview', path ? { $current_url: path } : undefined)
}

export function isFeatureEnabled(key: string): boolean {
  if (!ENABLED || !isBrowser()) {
    return false
  }
  return !!posthog.isFeatureEnabled(key)
}

export function getFeatureFlagPayload<T = unknown>(key: string): T | undefined {
  if (!ENABLED || !isBrowser()) {
    return undefined
  }
  // @ts-expect-error optional API depending on version
  return posthog.getFeatureFlagPayload?.(key) as T | undefined
}

export function onFeatureFlags(callback: () => void): () => void {
  if (!ENABLED || !isBrowser()) {
    return () => {}
  }
  posthog.onFeatureFlags(callback)
  return () => {
    // @ts-expect-error optional API depending on version
    posthog.offFeatureFlags?.(callback)
  }
}

export function resetPostHog(): void {
  if (!ENABLED || !isBrowser()) {
    return
  }
  posthog.reset()
}
